"use client";
import { cn } from "@/app/utils/cn";
import {
  useImperativeHandle,
  forwardRef,
  ComponentProps,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import useCountries from "@/app/lib/features/countries/countries.hook";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/app/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/shadcn/popover";
import { Button } from "@/app/components/shadcn/button";
import { Check, ChevronsUpDown } from "lucide-react";
import Image from "next/image";
import PhoneInputSkeleton from "../../skeletons/PhoneInputSkeleton";

interface PhoneInputProps extends Omit<
  ComponentProps<"input">,
  "type" | "value" | "onChange"
> {
  value?: string;
  onChange?: (value: string) => void;
  onCountryChange?: (countryId: number) => void;
  onMount?: (node: HTMLInputElement | null) => void;
  placeholder?: string;
  disabled?: boolean;
  defaultCountryId?: number;
}

interface PhoneInputRef {
  focus: () => void;
  blur: () => void;
}

const PhoneInput = forwardRef<PhoneInputRef, PhoneInputProps>(
  (
    {
      className,
      value = "",
      onChange,
      onCountryChange,
      onMount,
      defaultCountryId = 212,
      ...props
    },
    ref,
  ) => {
    const { countries, getCountries, isLoading, isIdle } = useCountries();
    const inputRef = useRef<HTMLInputElement>(null);
    const hasNotifiedCountryChange = useRef(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (isIdle) {
        getCountries();
      }
    }, [isIdle, getCountries]);

    const selectedCountry = useMemo(() => {
      if (!countries || countries.length === 0) return null;
      return countries.find((c) => c.id === defaultCountryId) || countries[0];
    }, [countries, defaultCountryId]);

    useEffect(() => {
      if (selectedCountry && !hasNotifiedCountryChange.current) {
        hasNotifiedCountryChange.current = true;
        onCountryChange?.(selectedCountry.id);
      }
    }, [selectedCountry, onCountryChange]);

    const formatPhoneNumber = (input: string, mask: string): string => {
      const digits = input.replace(/\D/g, "");
      let formatted = "";
      let digitIndex = 0;

      for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
        if (mask[i] === "#") {
          formatted += digits[digitIndex];
          digitIndex++;
        } else {
          formatted += mask[i];
        }
      }

      return formatted;
    };

    const getMaxDigits = (mask: string): number => {
      return (mask.match(/#/g) || []).length;
    };

    // Calculate display value from digits
    const displayValue = useMemo(() => {
      if (selectedCountry && value) {
        const digits = value.replace(/\D/g, "");
        return formatPhoneNumber(digits, selectedCountry.phone_mask);
      }
      return "";
    }, [value, selectedCountry]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedCountry) return;

      const cursorPosition = e.target.selectionStart || 0;
      const previousDisplayValue = displayValue;
      
      // Get only digits from input
      const digits = e.target.value.replace(/\D/g, "");
      
      // Limit to max digits allowed by mask
      const maxDigits = getMaxDigits(selectedCountry.phone_mask);
      const limitedDigits = digits.slice(0, maxDigits);
      
      // Send only digits to parent (this is what gets validated and sent to API)
      onChange?.(limitedDigits);

      // Format for display
      const formatted = formatPhoneNumber(limitedDigits, selectedCountry.phone_mask);

      requestAnimationFrame(() => {
        if (inputRef.current && formatted.length > previousDisplayValue.length) {
          const newCursorPosition =
            cursorPosition + (formatted.length - previousDisplayValue.length);
          inputRef.current.setSelectionRange(
            newCursorPosition,
            newCursorPosition,
          );
        }
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && displayValue.endsWith(" ")) {
        e.preventDefault();
        const newDisplay = displayValue.slice(0, -2);
        const digits = newDisplay.replace(/\D/g, "");
        onChange?.(digits);
      }
    };

    const handleCountryChange = (countryId: number) => {
      const country = countries?.find((c) => c.id === countryId);
      if (country) {
        onCountryChange?.(country.id);
        onChange?.("");
        setOpen(false);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    };

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (open) {
        setOpen(false);
      }
    };

    // Check if phone number is complete based on mask
    const isPhoneComplete = useMemo(() => {
      if (!selectedCountry || !value) return false;
      const digits = value.replace(/\D/g, "");
      const maxDigits = getMaxDigits(selectedCountry.phone_mask);
      return digits.length === maxDigits;
    }, [value, selectedCountry]);

    // Set aria-invalid attribute for validation (convert to proper type)
    const ariaInvalid = value && !isPhoneComplete ? true : undefined;

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          inputRef.current?.focus();
          const length = displayValue.length;
          inputRef.current?.setSelectionRange(length, length);
        },
        blur: () => {
          inputRef.current?.blur();
        },
      }),
      [displayValue.length],
    );

    if (isLoading || !selectedCountry) {
      return <PhoneInputSkeleton className={className} />;
    }

    return (
      <div className={cn("relative flex w-full h-9 md:h-12", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={`Ülke kodu seç: ${selectedCountry.phone_code}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(!open);
              }}
              className={cn(
                "selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex items-center rounded-l-md rounded-r-none border border-r-0 bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus:border-main focus:ring-main/50 focus:ring-[3px] hover:border-main/50 hover:ring-main/25 hover:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-muted-foreground whitespace-nowrap w-auto h-9 md:h-12 justify-between gap-2",
              )}
            >
              <div className="flex items-center gap-2">
                <Image
                  src={selectedCountry.photo_link}
                  alt={selectedCountry.name}
                  width={24}
                  height={18}
                  className="rounded-sm"
                />
                <span>{selectedCountry.phone_code}</span>
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-75 p-0" align="start">
            <Command>
              <CommandInput placeholder="Ülke ara..." />
              <CommandList>
                <CommandEmpty>Ülke bulunamadı.</CommandEmpty>
                <CommandGroup>
                  {countries?.map((country) => (
                    <CommandItem
                      key={country.id}
                      value={`${country.name} ${country.code} ${country.phone_code}`}
                      onSelect={() => handleCountryChange(country.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCountry.id === country.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <div className="flex items-center gap-2">
                        <Image
                          src={country.photo_link}
                          alt={country.name}
                          width={24}
                          height={18}
                          className="rounded-sm"
                        />
                        <span>{country.name}</span>
                        <span className="text-muted-foreground">
                          {country.phone_code}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <input
          ref={(node) => {
            if (inputRef.current !== node) {
              (
                inputRef as React.MutableRefObject<HTMLInputElement | null>
              ).current = node;
              onMount?.(node);
            }
          }}
          type="tel"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
          placeholder={selectedCountry.phone_mask.replace("#", "5").replace(/#/g, "X")}
          data-slot="phone-input"
          aria-invalid={ariaInvalid}
          className={cn(
            "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground h-9 md:h-12 dark:bg-input/30 border-input w-full min-w-0 rounded-l-none rounded-r-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            "focus-visible:border-main focus-visible:ring-main/50 focus-visible:ring-[3px]",
            "hover:border-main/50 hover:ring-main/25 hover:ring-[3px]",
            "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          )}
          {...props}
        />
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };